import {
  Box,
  Button,
  Center,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import React, { memo, useCallback, useMemo, useState } from "react";

import {
  PromotePendingEmailUsersMutation,
  PromotePendingEmailUsersMutationVariables,
  UserFragment,
} from "gql/graphql";
import useThrottledMutation from "modules/hooks/use-throttled-mutatation";
import { useUsers } from "modules/providers/users-provider";
import colors from "app/colors";
import PROMOTE_PENDING_EMAIL_USERS_MUTATION from "modules/messaging/_networking/promote-pending-email-users";
import { Virtuoso } from "react-virtuoso";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  messageThreadId: string;
  pendingEmailUserIdList: string[];
}

const AddPendingEmailUsersModal = ({
  messageThreadId,
  pendingEmailUserIdList,
}: IProps) => {
  const { closeModal } = useOverlay();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { getUsersById } = useUsers();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pendingUsers = useMemo(
    () => getUsersById(pendingEmailUserIdList),
    [pendingEmailUserIdList, getUsersById],
  );

  const [promotePendingEmailUsersAPI] = useThrottledMutation<
    PromotePendingEmailUsersMutation,
    PromotePendingEmailUsersMutationVariables
  >(PROMOTE_PENDING_EMAIL_USERS_MUTATION);

  const onSaveClicked = useCallback(async () => {
    setIsSubmitting(true);
    await promotePendingEmailUsersAPI({
      variables: {
        id: messageThreadId,
        userIdList: selectedIds,
      },
    });
    closeModal();
  }, [closeModal, messageThreadId, promotePendingEmailUsersAPI, selectedIds]);

  const onCheckboxChecked = useCallback(
    (userId: string) => () => {
      if (selectedIds.includes(userId)) {
        setSelectedIds(selectedIds.filter((id) => id !== userId));
      } else {
        setSelectedIds([...selectedIds, userId]);
      }
    },
    [selectedIds],
  );

  const renderEmailUser = useCallback(
    (_: number, user: UserFragment) => (
      <HStack>
        <Checkbox
          isChecked={selectedIds.includes(user.id) === true}
          onChange={onCheckboxChecked(user.id)}
        />
        <Box h="full" display="flex" alignItems="center">
          <Text fontWeight={700} color={colors.blackSecondary}>
            {user.email}
          </Text>
        </Box>
      </HStack>
    ),
    [onCheckboxChecked, selectedIds],
  );

  return (
    <Modal isOpen onClose={closeModal} size="md" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text>Add External Contacts</Text>
          </Center>
          <ModalCloseButton color={colors.grayMain} />
        </ModalHeader>
        <ModalBody py={6}>
          <Stack spacing={4} h="full">
            <Text
              fontSize={14}
              color={colors.gray50}
              textAlign="center"
              fontWeight={500}
            >
              Select contact(s) to add to this conversation. Note: Added outside
              parties can view all messages and attachments.
            </Text>
            <Virtuoso
              data={pendingUsers}
              itemContent={renderEmailUser}
              style={{ height: "300px" }}
            />
            <Button
              isDisabled={selectedIds.length === 0}
              w={250}
              alignSelf="center"
              onClick={onSaveClicked}
            >
              {isSubmitting ? <Spinner /> : "Save"}
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AddPendingEmailUsersModal);
